import React, { useEffect, useState, useRef } from 'react';

interface SystemStatsProps {
  apiUrl: string;
  getAuthHeaders: () => HeadersInit;
}

interface Stats {
  cpu: number | null;
  mem: number | null;
  fps: number | null;
}

function statClass(pct: number | null): string {
  if (pct === null) return '';
  if (pct > 85) return 'stat-crit';
  if (pct > 60) return 'stat-warn';
  return 'stat-ok';
}

const SystemStats: React.FC<SystemStatsProps> = ({ apiUrl, getAuthHeaders }) => {
  const [stats, setStats] = useState<Stats>({ cpu: null, mem: null, fps: null });
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    const poll = () => {
      fetch(`${apiUrl}/api/drone/pipeline-stats`, { headers: getAuthHeaders() })
        .then(r => r.ok ? r.json() : null)
        .then(d => {
          if (!mountedRef.current) return;
          if (!d) { setStats({ cpu: null, mem: null, fps: null }); return; }
          setStats({
            cpu: d.system?.cpu_pct ?? null,
            mem: d.system?.mem_pct ?? null,
            fps: d.mjpeg?.fps ?? null,
          });
        })
        .catch(() => {
          if (mountedRef.current) setStats({ cpu: null, mem: null, fps: null });
        });
    };

    poll();
    const id = setInterval(poll, 3000);
    return () => { mountedRef.current = false; clearInterval(id); };
  }, [apiUrl, getAuthHeaders]);

  const fmt = (v: number | null) => v !== null ? `${Math.round(v)}%` : '--';
  const fmtFps = (v: number | null) => v !== null ? `${v.toFixed(1)}` : '--';

  return (
    <div className="system-stats" title="Server CPU / Memory / Stream FPS">
      <span className={`stat-value ${statClass(stats.cpu)}`} title="Server CPU usage — percentage of all cores currently in use"><span className="stat-label">CPU </span>{fmt(stats.cpu)}</span>
      <span className="stat-sep">|</span>
      <span className={`stat-value ${statClass(stats.mem)}`} title="Server RAM usage — percentage of total physical memory in use"><span className="stat-label">MEM </span>{fmt(stats.mem)}</span>
      <span className="stat-sep">|</span>
      <span className="stat-value stat-fps" title="MJPEG stream delivery rate — frames per second sent to video viewers">{fmtFps(stats.fps)}<span className="stat-label"> fps</span></span>
    </div>
  );
};

export default SystemStats;
